import { Component, HostListener, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { firstValueFrom, map, take } from 'rxjs';
import { LucideAngularModule, Menu, X, Instagram, Facebook, Youtube, MessageCircle, Twitter, User, LogOut, ClipboardList, Calendar } from 'lucide-angular';
import { AuthService } from '../../services/auth.service';

interface NavLink {
  label: string;
  route: string;
  exact?: boolean;
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, LucideAngularModule],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent {
  private authService = inject(AuthService);
  private router = inject(Router);

  userProfile$ = this.authService.userProfile$;
  isAdmin$ = this.userProfile$.pipe(map((profile) => profile?.role === 'admin'));
  showMobileMenu = false;

  readonly MenuIcon = Menu;
  readonly CloseIcon = X;
  readonly InstagramIcon = Instagram;
  readonly FacebookIcon = Facebook;
  readonly YoutubeIcon = Youtube;
  readonly WhatsAppIcon = MessageCircle;
  readonly UserIcon = User;
  readonly LogOutIcon = LogOut;
  readonly RegistrationIcon = ClipboardList;
  readonly BookingIcon = Calendar;
  readonly publicNavLinks: NavLink[] = [
    { label: 'Home', route: '/', exact: true },
    { label: 'About Us', route: '/about' },
    { label: 'Registration', route: '/register' },
    { label: 'Book Slots', route: '/booking' },
    { label: 'Programs', route: '/programs' },
    { label: 'Gallery', route: '/gallery' },
    { label: 'Store', route: '/store' },
    { label: 'Instructor & Founder', route: '/instructors-founders' }
  ];
  readonly adminNavLinks: NavLink[] = [
    // { label: 'Dashboard', route: '/admin', exact: true },
    { label: 'All Bookings', route: '/admin/bookings' },
    { label: 'New Registration', route: '/admin/new-registrations' },
    { label: 'Block Slots', route: '/admin/block-slot' },
    { label: 'Gallery', route: '/admin/gallery' },
  ];

  async goToLogoDestination(): Promise<void> {
    const currentUser = await this.authService.getCurrentUser();
    if (!currentUser) {
      this.router.navigate(['/']);
      return;
    }

    const profile = await firstValueFrom(
      this.authService.getUserProfile(currentUser.uid).pipe(take(1)),
    );
    const target = profile?.role === 'admin' ? '/admin/bookings' : '/';
    this.router.navigate([target]);
  }

  async logout(): Promise<void> {
    try {
      await this.authService.logout();
      this.closeMobileMenu();
      this.router.navigate(['/signin']);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }

  toggleMobileMenu(): void {
    this.showMobileMenu = !this.showMobileMenu;
    document.body.style.overflow = this.showMobileMenu ? 'hidden' : '';
  }

  closeMobileMenu(): void {
    this.showMobileMenu = false;
    document.body.style.overflow = '';
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.showMobileMenu) {
      this.closeMobileMenu();
    }
  }
}
